/**
 * The Tickets tab of a Well Testing job (fieldiq-api#1253, WT-4). JobSections.tsx
 * is not edited; these sit beside it, and JobDetailScreen.tsx only gains the tab.
 *
 *   WtJobTicketsSection  the job's tickets, and "Create ticket" — per job, or
 *                        per day with a date when the job is ticketed per day.
 *                        Drafting reads the shift logs; a refusal (no logs,
 *                        a ticket already, the other scope) is a sentence.
 *   WtRentInBillingPanel the billing rate of each rent-in: equipment-unit or
 *                        other rates only, from this job's customer card or the
 *                        default card (`rate_not_rent_in`, `rate_other_customer`).
 *                        A rent-in bills only on days with a shift log.
 */
import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { useCreateWtTicket, useWtRates, useWtRentInBillingRates, useWtTickets, useSetWtRentInBillingRate } from '../../../hooks/useWtTickets';
import { queryLoadFailure } from '../../../lib/load-failure';
import { wtScopeText, wtTicketRefusal, wtTicketStatusSkin } from '../../../lib/wt-tickets';
import type { WtJob, WtRentIn } from '../../../types/well-testing';
import type { WtTicket } from '../../../types/wt-tickets';
import { useOpenRecord } from '../../downhole/recordPage';
import { WT_DATE_INPUT, WT_INPUT, WT_PRIMARY, WT_SECONDARY, WtBadge, WtEmpty, WtField, WtFormError, WtPanel, WtRegister, type WtColumn } from '../WtBits';
import { wtTicketTotalText } from './TicketsScreen';
import { wtRateOption } from './TicketBits';

const columns: WtColumn<WtTicket>[] = [
  { key: 'number', header: 'Ticket', render: (t) => <span className="font-mono font-extrabold text-white">{t.ticketNumber || '—'}</span> },
  { key: 'scope', header: 'Covers', render: (t) => wtScopeText(t) },
  { key: 'lines', header: 'Lines', align: 'right', render: (t) => String(t.lineCount ?? '—') },
  { key: 'status', header: 'Status', render: (t) => <WtBadge skin={wtTicketStatusSkin(t.status)} value={t.status} /> },
  { key: 'total', header: 'Total', align: 'right', render: (t) => <span className="font-mono">{wtTicketTotalText(t)}</span> },
];

export function WtJobTicketsSection({ job, canWrite, today }: { job: WtJob; canWrite: boolean; today: string | null }) {
  const openRecord = useOpenRecord();
  const query = useWtTickets({ job_id: job.id });
  const create = useCreateWtTicket();
  const perDay = job.ticketMode === 'per_day';
  const [creating, setCreating] = useState(false);
  const [day, setDay] = useState(today ?? '');
  const [error, setError] = useState<string | null>(null);
  const tickets = query.data?.data ?? [];

  const draft = () => {
    setError(null);
    if (perDay && !/^\d{4}-\d{2}-\d{2}$/.test(day)) return setError('This job is ticketed per day. Pick the day to ticket.');
    create.mutate(
      { jobId: job.id, workDate: perDay ? day : undefined },
      {
        onSuccess: (r) => {
          toast.success(r.data ? `${r.data.ticketNumber} drafted from the shift logs` : 'Ticket drafted');
          setCreating(false);
          if (r.data) openRecord(`/well-testing/tickets/${r.data.id}`);
        },
        onError: (e) => setError(wtTicketRefusal(e, 'The ticket could not be drafted.')),
      },
    );
  };

  return (
    <WtPanel
      eyebrow="Field tickets"
      title={perDay ? 'Ticketed per day' : 'Ticketed per job'}
      testId="wt-job-tickets"
      actions={
        canWrite && !creating ? (
          <button type="button" className={WT_PRIMARY} onClick={() => { setError(null); setCreating(true); }}>
            <Plus className="size-4" aria-hidden="true" />
            Create ticket
          </button>
        ) : undefined
      }
    >
      {creating && (
        <div data-testid="wt-job-ticket-create" className="space-y-3 border-b border-fieldiq-border px-4 py-4">
          <p className="text-sm text-fieldiq-text">
            {perDay
              ? 'Drafts one ticket for the day you pick, from that day’s shift logs. Each line is priced from the rate card where a rate fits.'
              : 'Drafts one ticket covering every day of the job, from its shift logs. Each line is priced from the rate card where a rate fits.'}
          </p>
          <WtFormError message={error} />
          {perDay && (
            <div className="grid gap-3 sm:grid-cols-[14rem]">
              <WtField label="Day to ticket *">
                <input className={WT_DATE_INPUT} type="date" aria-label="Day to ticket" value={day} onChange={(e) => setDay(e.target.value)} />
              </WtField>
            </div>
          )}
          <div className="flex flex-wrap justify-end gap-2">
            <button type="button" className={WT_SECONDARY} onClick={() => setCreating(false)} disabled={create.isPending}>
              Cancel
            </button>
            <button type="button" className={WT_PRIMARY} onClick={draft} disabled={create.isPending}>
              {create.isPending ? 'Drafting…' : 'Draft ticket'}
            </button>
          </div>
        </div>
      )}
      <WtRegister
        query={query}
        rows={tickets}
        columns={columns}
        rail={(t) => wtTicketStatusSkin(t.status).rail}
        onOpen={(t) => openRecord(`/well-testing/tickets/${t.id}`)}
        subject="field tickets"
        testId="wt-job-ticket"
        minWidth="min-w-[640px]"
        empty={<WtEmpty>No tickets for this job yet.</WtEmpty>}
        card={(t) => (
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate font-mono text-sm font-extrabold text-white">{t.ticketNumber}</p>
              <p className="mt-0.5 text-xs text-fieldiq-muted">{wtScopeText(t)} · Total {wtTicketTotalText(t)}</p>
            </div>
            <WtBadge skin={wtTicketStatusSkin(t.status)} value={t.status} />
          </div>
        )}
      />
    </WtPanel>
  );
}

function RentInRateRow({
  rentIn,
  current,
  loading,
  failed,
  options,
  canWrite,
}: {
  rentIn: WtRentIn;
  current: { id: string; label: string } | null;
  loading: boolean;
  failed: boolean;
  options: { id: string; label: string }[];
  canWrite: boolean;
}) {
  const set = useSetWtRentInBillingRate();
  const [error, setError] = useState<string | null>(null);
  return (
    <li data-testid="wt-rent-in-billing-row" className="space-y-2 px-4 py-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold text-white">{rentIn.description || '—'}</p>
          <p className="text-xs text-fieldiq-muted">
            {rentIn.vendorName || '—'}
            {rentIn.unitNumber ? ` · ${rentIn.unitNumber}` : ''}
          </p>
        </div>
        <div className="w-full sm:w-[28rem]">
          {canWrite ? (
            <select
              className={WT_INPUT}
              aria-label={`Billing rate for ${rentIn.description}`}
              value={current?.id ?? ''}
              disabled={loading || failed || set.isPending}
              onChange={(e) => {
                setError(null);
                set.mutate(
                  { rentInId: rentIn.id, rateId: e.target.value || null },
                  {
                    onSuccess: () => toast.success(e.target.value ? 'Billing rate set' : 'Billing rate cleared'),
                    onError: (err) => setError(wtTicketRefusal(err, 'The billing rate could not be set.')),
                  },
                );
              }}
            >
              <option value="">{loading ? 'Loading…' : 'Not billed from the rate card'}</option>
              {current && !options.some((o) => o.id === current.id) && <option value={current.id}>{current.label}</option>}
              {options.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
            </select>
          ) : (
            <p className="text-sm text-fieldiq-text">{loading ? '…' : current?.label ?? '—'}</p>
          )}
        </div>
      </div>
      {failed && <WtFormError message="This rent-in's billing rate could not be read." />}
      <WtFormError message={error} />
    </li>
  );
}

export function WtRentInBillingPanel({ job, rentIns, canWrite }: { job: WtJob; rentIns: WtRentIn[]; canWrite: boolean }) {
  const ratesQuery = useWtRates(undefined, canWrite);
  const billing = useWtRentInBillingRates(rentIns.map((r) => r.id));
  const options = (ratesQuery.data?.data ?? [])
    .filter((r) => (r.kind === 'unit' || r.kind === 'other') && (r.customerId === null || r.customerId === job.customerId))
    .map((r) => ({ id: r.id, label: wtRateOption(r) }));
  return (
    <WtPanel eyebrow="Billing" title="Rent-in billing rates" testId="wt-rent-in-billing">
      <p className="border-b border-fieldiq-border px-4 py-3 text-sm text-fieldiq-muted">
        What the customer is billed for each rent-in, from the rate card — an equipment-unit or other rate on this customer’s card or the default card. A rent-in
        bills only on days with a shift log, and one with no billing rate drafts onto a ticket unpriced.
      </p>
      {canWrite && queryLoadFailure(ratesQuery) && <div className="px-4 pt-3"><WtFormError message="The rate card could not be read, so a billing rate cannot be picked right now." /></div>}
      {!rentIns.length ? (
        <WtEmpty>No rent-ins on this job.</WtEmpty>
      ) : (
        <ul className="divide-y divide-fieldiq-border">
          {rentIns.map((ri, i) => {
            const q = billing[i];
            const rate = q?.data?.data?.rate ?? null;
            return (
              <RentInRateRow
                key={ri.id}
                rentIn={ri}
                current={rate ? { id: rate.id, label: wtRateOption(rate) } : null}
                loading={!!q?.isLoading}
                failed={!!q && !!queryLoadFailure(q)}
                options={options}
                canWrite={canWrite}
              />
            );
          })}
        </ul>
      )}
    </WtPanel>
  );
}

